<script setup lang="ts">
  import type { EnumValues } from '@travelata-libs/utils';
  import { onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
  import { BottomSheet, FloatingTrigger, FloatingPlacement } from '@/components';
  import PopoverDesktop from '@/components/Popover/PopoverDesktop.vue';
  import { useTrigger } from '@/composables';

  interface PopoverProps {
    width?: string;
    trigger?: EnumValues<typeof FloatingTrigger>;
    placement?: EnumValues<typeof FloatingPlacement>;
    title?: string;
    closable?: boolean;
    visible?: boolean;
    zIndex?: number;
    disabled?: boolean;
    mobile?: boolean;
  }

  interface PopoverEmits {
    (e: 'change-visible', value: boolean): void;
    (e: 'close'): void;
  }

  const props = withDefaults(defineProps<PopoverProps>(), {
    width: 'auto',
    title: '',
    visible: false,
    closable: false,
    trigger: FloatingTrigger.Click,
    placement: FloatingPlacement.Bottom,
    zIndex: 5,
    disabled: false,
    mobile: false,
  });

  const emit = defineEmits<PopoverEmits>();

  const referenceElement = ref<HTMLElement>();
  const floatingElement = ref<HTMLElement>();

  const bottomSheetIsVisible = ref(props.visible);

  const onClick = () => {
    bottomSheetIsVisible.value = !bottomSheetIsVisible.value;
  };

  const { initTrigger, destroyTrigger } = useTrigger({
    referenceElement,
    floatingElement,
    callback: {
      onClick,
    },
  });

  watch(
    () => [props.trigger, props.mobile],
    async () => {
      destroyTrigger();
      if (props.mobile) {
        await nextTick();
        initTrigger(props.trigger);
      }
    },
  );

  watch(
    () => props.visible,
    async () => {
      if (props.mobile) {
        bottomSheetIsVisible.value = props.visible;
      }
    },
  );

  watch(bottomSheetIsVisible, () => {
    emit('change-visible', bottomSheetIsVisible.value);
    if (!bottomSheetIsVisible.value) {
      emit('close');
    }
  });

  const close = () => {
    if (props.mobile) {
      bottomSheetIsVisible.value = false;
    } else {
      floatingElement.value?.close();
    }
  };

  const open = () => {
    if (props.mobile) {
      bottomSheetIsVisible.value = true;
    } else {
      floatingElement.value?.open();
    }
  };

  onMounted(() => {
    initTrigger(props.trigger);
  });

  onBeforeUnmount(() => {
    destroyTrigger();
  });

  defineExpose({
    close,
    open,
  });
</script>

<template>
  <div v-if="mobile">
    <div ref="referenceElement" data-test-id="reference">
      <slot name="reference" />
    </div>
    <BottomSheet v-if="bottomSheetIsVisible" ref="floatingElement" :title="title" :z-index="zIndex" @close="close">
      <template #header>
        <slot name="header" />
      </template>
      <slot />
      <template #footer>
        <slot name="footer" />
      </template>
    </BottomSheet>
  </div>
  <PopoverDesktop
    v-else
    ref="floatingElement"
    :width="width"
    :trigger="trigger"
    :placement="placement"
    :title="title"
    :closable="closable"
    :visible="visible"
    :z-index="zIndex"
    :disabled="disabled"
    @change-visible="emit('change-visible', $event)"
    @close="emit('close')"
  >
    <template #reference>
      <slot name="reference" />
    </template>
    <template #header>
      <slot name="header" />
    </template>
    <slot />
    <template #footer>
      <slot name="footer" />
    </template>
  </PopoverDesktop>
</template>
